import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useBanners } from './banners';
import { CreativityContentData } from '../types/banner';

const getFolderPathFromLocation = (pathname: string): string | undefined => {
  const rawPath = pathname.replace('/library', '').replace(/^\//, '');
  return rawPath ? decodeURIComponent(rawPath) : undefined;
};

const PAGE_LIMIT = 60;

interface LibraryContextType {
  banners: CreativityContentData[];
  folders: string[];
  currentFolder: string | undefined;
  // Carga de la primera página (cambio de carpeta / refresh).
  isLoading: boolean;
  // Carga de páginas siguientes (scroll infinito).
  isLoadingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
  // Recarga la primera página de la carpeta actual (botón "Actualizar").
  refresh: () => void;
}

const LibraryContext = createContext<LibraryContextType | undefined>(undefined);

export const LibraryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { getBannersByFolder, folders, loadFolders } = useBanners();

  const [banners, setBanners] = useState<CreativityContentData[]>([]);
  const [currentFolder, setCurrentFolder] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);

  // Carpeta que estamos cargando actualmente; evita que respuestas viejas pisen una carpeta nueva.
  const activeFolderRef = useRef<string | undefined>(undefined);
  // Evita disparar varias cargas de la misma página en simultáneo.
  const loadingRef = useRef(false);

  const loadFirstPage = useCallback(
    async (folderPath: string | undefined) => {
      activeFolderRef.current = folderPath;
      setIsLoading(true);
      setBanners([]);
      setNextToken(undefined);
      loadingRef.current = true;

      const page = await getBannersByFolder(folderPath, { limit: PAGE_LIMIT });

      // Si la carpeta cambió mientras cargábamos, descartar este resultado.
      if (activeFolderRef.current !== folderPath) return;

      setBanners(page.items);
      setNextToken(page.nextToken);
      setIsLoading(false);
      loadingRef.current = false;
    },
    [getBannersByFolder]
  );

  // Cargar la primera página cuando cambia la URL (reset de acumulado + token).
  useEffect(() => {
    const folderPath = getFolderPathFromLocation(location.pathname);
    setCurrentFolder(folderPath);
    loadFirstPage(folderPath);
    loadFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const refresh = useCallback(() => {
    loadFirstPage(activeFolderRef.current);
    loadFolders();
  }, [loadFirstPage, loadFolders]);

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !nextToken) return;

    const folderPath = activeFolderRef.current;
    loadingRef.current = true;
    setIsLoadingMore(true);

    try {
      const page = await getBannersByFolder(folderPath, { limit: PAGE_LIMIT, nextToken });

      // Descartar si cambiamos de carpeta durante la carga.
      if (activeFolderRef.current !== folderPath) return;

      setBanners(prev => [...prev, ...page.items]);
      setNextToken(page.nextToken);
    } finally {
      setIsLoadingMore(false);
      loadingRef.current = false;
    }
  }, [getBannersByFolder, nextToken]);

  const value: LibraryContextType = {
    banners,
    folders,
    currentFolder,
    isLoading,
    isLoadingMore,
    hasMore: !!nextToken,
    loadMore,
    refresh
  };

  return <LibraryContext.Provider value={value}>{children}</LibraryContext.Provider>;
};

export const useLibrary = (): LibraryContextType => {
  const context = useContext(LibraryContext);
  if (context === undefined) {
    throw new Error('useLibrary must be used within a LibraryProvider');
  }
  return context;
};
